import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Routes, Route } from 'react-router-dom';
import { useAuth, useTheme, useTenant, useConfirm, useLanguage } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Sun, Moon, Home, Calendar as CalendarIcon, Image, Video, MessageSquare, 
  Settings, LogOut, Menu, X, Users, Ticket, DollarSign, Mail, Plus,
  Edit, Trash2, Eye, EyeOff, Check, QrCode, LayoutDashboard, Megaphone, Star, 
  Upload, Loader2, Image as ImageIcon, CreditCard, ExternalLink, AlertCircle, FileText, Download, Info
} from 'lucide-react';
import SenArtisteLogo from '../../components/SenArtisteLogo';
import { AIWriter } from '../../components/ai/AIWriter';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Management = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout, api, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tenant, fetchTenant } = useTenant();
  const confirm = useConfirm();
  const { t } = useLanguage();
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({});
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [banners, setBanners] = useState([]);
  const [media, setMedia] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [artistWithdrawalInfo, setArtistWithdrawalInfo] = useState({});
  
  // Form states
  const defaultTicketType = { name: '', price: '', quantity: '' };
  const [eventForm, setEventForm] = useState({ 
    title: '', 
    description: '', 
    dateTime: new Date(), 
    ticketPrice: 0, 
    ticketTypes: [{ ...defaultTicketType, name: 'Standard' }],  // Types de billets multiples
    commissionPaidBy: 'artist',  // 'artist' ou 'fan'
    currency: 'FCFA', 
    location: '', 
    isPublished: false, 
    maxCapacity: '' 
  });
  const [postForm, setPostForm] = useState({ title: '', content: '', isPublished: false });
  const [bannerForm, setBannerForm] = useState({ title: '', description: '', imageUrl: '', contactPhone: '', contactEmail: '', shopUrl: '', isActive: true });
  const [mediaForm, setMediaForm] = useState({ type: 'PHOTO', title: '', url: '' });
  const [managerForm, setManagerForm] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [scannerForm, setScannerForm] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  
  // Dialog states
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Reversement states
  const [reversementDialogOpen, setReversementDialogOpen] = useState(false);
  const [reversementEventId, setReversementEventId] = useState(null);
  const [reversementMethod, setReversementMethod] = useState('wave');
  const [reversementLoading, setReversementLoading] = useState(false);
  
  // Managers state
  const [managers, setManagers] = useState([]);
  
  // Scanners state
  const [scanners, setScanners] = useState([]);
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const fileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);

  useEffect(() => {
    // Attendre que l'authentification soit chargée
    if (authLoading) return;
    
    if (!user || !['ARTISTE', 'MANAGER'].includes(user.role)) {
      navigate(`/${artistSlug}/login`);
      return;
    }
    fetchTenant(artistSlug);
    fetchAllData();
    fetchStripeStatus();
    fetchManagers();
    fetchScanners();
    
    // Gérer le retour de Stripe onboarding
    const searchParams = new URLSearchParams(location.search);
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Configuration Stripe terminée ! Vérification du statut en cours...');
      // Rafraîchir le statut Stripe après un délai pour laisser Stripe finaliser
      setTimeout(() => {
        fetchStripeStatus();
      }, 2000);
      // Nettoyer l'URL
      navigate(`/${artistSlug}/management`, { replace: true });
    } else if (stripeParam === 'refresh') {
      toast.info('Session expirée. Veuillez recommencer la configuration Stripe.');
      navigate(`/${artistSlug}/management`, { replace: true });
    }
  }, [artistSlug, user, authLoading, location.search]);

  const fetchAllData = async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const [statsRes, eventsRes, postsRes, messagesRes, bannersRes, mediaRes] = await Promise.all([
        api.get(`/dashboard/tenant/${user.tenantId}`),
        api.get(`/events/tenant/${user.tenantId}/all-earnings`),
        api.get(`/posts/tenant/${user.tenantId}?include_unpublished=true`),
        api.get(`/messages/tenant/${user.tenantId}`),
        api.get(`/banners/tenant/${user.tenantId}`),
        api.get(`/media/tenant/${user.tenantId}`),
      ]);
      
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
      setArtistWithdrawalInfo(eventsRes.data.artistWithdrawalInfo || {});
      setPosts(postsRes.data);
      setMessages(messagesRes.data);
      setBanners(bannersRes.data);
      setMedia(mediaRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour effectuer un retrait
  const handleWithdraw = async (eventId, currency, withdrawalMethod = null) => {
    try {
      const res = await api.post(`/events/${eventId}/withdraw`, { 
        currency,
        withdrawalMethod 
      });
      toast.success(res.data.message);
      fetchAllData(); // Rafraîchir les données
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du retrait');
    }
  };

  // Fonction pour demander un reversement DKR (FCFA)
  const handleRequestReversement = async () => {
    if (!reversementEventId) return;
    
    // Trouver l'événement pour obtenir les montants
    const event = events.find(e => e.id === reversementEventId);
    if (!event) return;
    
    // Déterminer le numéro de téléphone selon la méthode
    const mobile = reversementMethod === 'wave' 
      ? artistWithdrawalInfo?.waveNumber 
      : artistWithdrawalInfo?.orangeMoneyNumber;
    
    if (!mobile) {
      toast.error(`Veuillez d'abord configurer votre numéro ${reversementMethod === 'wave' ? 'Wave' : 'Orange Money'}`);
      return;
    }
    
    // Obtenir le nom de l'artiste
    const recipientName = tenant?.nomDeScene || tenant?.prenom || 'Artiste';
    
    setReversementLoading(true);
    try {
      const res = await api.post('/dkr/request-payout', {
        event_id: reversementEventId,
        payout_method: reversementMethod,
        mobile: mobile,
        recipient_name: recipientName
      });
      toast.success(res.data.message || `Reversement de ${res.data.amount?.toLocaleString()} FCFA envoyé avec succès !`);
      setReversementDialogOpen(false);
      fetchAllData();
    } catch (error) {
      // Gestion des erreurs avec message user-friendly
      const errorData = error.response?.data?.detail;
      if (typeof errorData === 'object' && errorData.error === 'payout_failed') {
        toast.error(errorData.message || 'Erreur lors du reversement. L\'admin vous contactera.');
      } else if (typeof errorData === 'object' && errorData.error === 'system_error') {
        toast.error(errorData.message || 'Erreur technique. L\'admin a été notifié.');
      } else {
        toast.error(errorData || error.response?.data?.detail || 'Erreur lors du reversement');
      }
    } finally {
      setReversementLoading(false);
    }
  };

  const fetchEventTickets = async (eventId) => {
    try {
      const res = await api.get(`/tickets/event/${eventId}`);
      setTickets(res.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  // Fetch Stripe status
  const fetchStripeStatus = async () => {
    if (!user?.tenantId) return;
    try {
      const res = await api.get(`/artists/tenant/${user.tenantId}/stripe/status`);
      setStripeStatus(res.data);
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    }
  };

  // Initiate Stripe onboarding
  const handleStripeOnboarding = async () => {
    try {
      const res = await api.post(`/artists/tenant/${user.tenantId}/stripe/onboarding`, {
        origin_url: window.location.origin
      });
      
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      } else if (res.data.status === 'active') {
        toast.success('Compte Stripe déjà actif !');
        fetchStripeStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur Stripe');
    }
  };

  // Générer la carte de visite PDF (format A5 flyer)
  const generateBusinessCard = async () => {
    try {
      toast.info('Génération de la carte de visite...');
      
      // Récupérer les données de l'artiste avec QR code
      const res = await api.get(`/artists/tenant/${user.tenantId}/business-card`);
      const artistData = res.data.artist;
      
      // Importer jsPDF dynamiquement
      const { jsPDF } = await import('jspdf');
      
      // Format A5 en mm (148 x 210)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5'
      });
      
      const pageWidth = 148;
      const pageHeight = 210;
      
      // Background gradient (violet Sen Artiste)
      doc.setFillColor(147, 51, 234); // #9333EA
      doc.rect(0, 0, pageWidth, 70, 'F');
      
      // Decoration triangles
      doc.setFillColor(124, 58, 237); // #7C3AED
      doc.triangle(0, 70, 50, 70, 0, 100, 'F');
      doc.triangle(pageWidth, 70, pageWidth - 50, 70, pageWidth, 100, 'F');
      
      // Logo Sen Artiste (texte)
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('SEN ARTISTE', pageWidth / 2, 15, { align: 'center' });
      
      // Sparkles decoration
      doc.setFontSize(8);
      doc.text('✦', 20, 15);
      doc.text('✦', pageWidth - 20, 15);
      
      // Nom de scène (grand)
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text(artistData.nomDeScene || 'Artiste', pageWidth / 2, 45, { align: 'center' });
      
      // Nom complet
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      const fullName = `${artistData.prenom || ''} ${artistData.nom || ''}`.trim();
      if (fullName) {
        doc.text(fullName, pageWidth / 2, 55, { align: 'center' });
      }
      
      // Section blanche
      doc.setFillColor(255, 255, 255);
      doc.setTextColor(50, 50, 50);
      
      // Bio (si disponible)
      if (artistData.bio) {
        doc.setFontSize(10);
        const bioLines = doc.splitTextToSize(artistData.bio, pageWidth - 30);
        doc.text(bioLines.slice(0, 3), pageWidth / 2, 85, { align: 'center' });
      }
      
      // QR Code
      if (artistData.qrCodeUrl) {
        try {
          const qrUrl = artistData.qrCodeUrl.startsWith('http') 
            ? artistData.qrCodeUrl 
            : `${process.env.REACT_APP_BACKEND_URL}${artistData.qrCodeUrl}`;
          
          // Charger l'image QR code
          const qrImg = new Image();
          qrImg.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            qrImg.onload = resolve;
            qrImg.onerror = reject;
            qrImg.src = qrUrl;
          });
          
          // Ajouter le QR code au PDF
          const qrSize = 45;
          const qrX = (pageWidth - qrSize) / 2;
          doc.addImage(qrImg, 'PNG', qrX, 110, qrSize, qrSize);
        } catch (qrError) {
          console.error('Erreur chargement QR:', qrError);
        }
      }
      
      // Texte sous QR code
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Scannez pour découvrir ma musique', pageWidth / 2, 162, { align: 'center' });
      
      // URL de la page
      doc.setFontSize(11);
      doc.setTextColor(147, 51, 234);
      doc.setFont('helvetica', 'bold');
      doc.text(artistData.artistPageUrl || `sen-artiste.com/${artistData.slug}`, pageWidth / 2, 172, { align: 'center' });
      
      // Contact
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      let contactY = 185;
      if (artistData.emailContact) {
        doc.text(`✉ ${artistData.emailContact}`, pageWidth / 2, contactY, { align: 'center' });
        contactY += 6;
      }
      if (artistData.telephoneContact) {
        doc.text(`☎ ${artistData.telephoneContact}`, pageWidth / 2, contactY, { align: 'center' });
      }
      
      // Footer
      doc.setFillColor(147, 51, 234);
      doc.rect(0, pageHeight - 10, pageWidth, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.text('www.sen-artiste.com', pageWidth / 2, pageHeight - 4, { align: 'center' });
      
      // Sauvegarder
      doc.save(`carte-visite-${artistData.slug}.pdf`);
      toast.success('Carte de visite téléchargée !');
      
    } catch (error) {
      console.error('Erreur génération carte:', error);
      toast.error('Erreur lors de la génération de la carte de visite');
    }
  };

  // Fetch managers
  const fetchManagers = async () => {
    if (!user?.tenantId || user?.role !== 'ARTISTE') return;
    try {
      const res = await api.get(`/artists/tenant/${user.tenantId}/managers`);
      setManagers(res.data);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  // Fetch scanners
  const fetchScanners = async () => {
    if (!user?.tenantId || !['ARTISTE', 'MANAGER'].includes(user?.role)) return;
    try {
      const res = await api.get(`/artists/tenant/${user.tenantId}/scanners`);
      setScanners(res.data);
    } catch (error) {
      console.error('Error fetching scanners:', error);
    }
  };

  // Event CRUD
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    
    // Avertissement si événement en EUR sans compte Stripe actif
    if (eventForm.currency === 'EUR' && !stripeStatus?.stripe_account_enabled) {
      const proceed = await confirm({
        title: t('stripeWarningTitle'),
        message: t('stripeWarningDesc'),
        type: 'warning',
        confirmText: t('continueAnyway'),
        cancelText: t('cancel')
      });
      if (!proceed) return;
    }
    
    // Valider les types de billets
    const validTicketTypes = eventForm.ticketTypes.filter(tt => tt.name && tt.price);
    if (validTicketTypes.length === 0) {
      toast.error('Veuillez ajouter au moins un type de billet avec un nom et un prix');
      return;
    }
    
    try {
      const payload = {
        ...eventForm,
        dateTime: eventForm.dateTime.toISOString(),
        ticketPrice: parseFloat(validTicketTypes[0].price) || 0,  // Rétrocompatibilité
        ticketTypes: validTicketTypes.map(tt => ({
          name: tt.name,
          price: parseFloat(tt.price),
          quantity: tt.quantity ? parseInt(tt.quantity) : null
        })),
        commissionPaidBy: eventForm.commissionPaidBy,
        maxCapacity: eventForm.maxCapacity ? parseInt(eventForm.maxCapacity) : null,
      };
      
      if (editingItem) {
        await api.put(`/events/${editingItem.id}`, payload);
        toast.success('Événement mis à jour');
      } else {
        await api.post('/events/', payload);
        toast.success('Événement créé');
      }
      
      setEventDialogOpen(false);
      setEditingItem(null);
      resetEventForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const resetEventForm = () => {
    setEventForm({ 
      title: '', 
      description: '', 
      dateTime: new Date(), 
      ticketPrice: 0, 
      ticketTypes: [{ name: 'Standard', price: '', quantity: '' }],
      commissionPaidBy: 'artist',
      currency: 'FCFA', 
      location: '', 
      isPublished: false, 
      maxCapacity: '' 
    });
  };

  const addTicketType = () => {
    if (eventForm.ticketTypes.length >= 5) {
      toast.error('Maximum 5 types de billets');
      return;
    }
    setEventForm(p => ({
      ...p,
      ticketTypes: [...p.ticketTypes, { name: '', price: '', quantity: '' }]
    }));
  };

  const removeTicketType = (index) => {
    if (eventForm.ticketTypes.length <= 1) {
      toast.error('Au moins un type de billet requis');
      return;
    }
    setEventForm(p => ({
      ...p,
      ticketTypes: p.ticketTypes.filter((_, i) => i !== index)
    }));
  };

  const updateTicketType = (index, field, value) => {
    setEventForm(p => ({
      ...p,
      ticketTypes: p.ticketTypes.map((tt, i) => 
        i === index ? { ...tt, [field]: value } : tt
      )
    }));
  };

  // Calcul du montant net pour l'artiste
  const calculateNetAmount = (price, commissionPaidBy) => {
    const numPrice = parseFloat(price) || 0;
    if (commissionPaidBy === 'fan') {
      return numPrice;  // L'artiste reçoit 100%
    } else {
      return numPrice * 0.95;  // L'artiste reçoit 95%
    }
  };

  const handleDeleteEvent = async (id) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteEventDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success('Événement supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Post CRUD
  const handleSavePost = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/posts/${editingItem.id}`, postForm);
        toast.success('Actualité mise à jour');
      } else {
        await api.post('/posts', postForm);
        toast.success('Actualité créée');
      }
      
      setPostDialogOpen(false);
      setEditingItem(null);
      setPostForm({ title: '', content: '', isPublished: false });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePost = async (id) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deletePostDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success('Actualité supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Banner CRUD
  const handleSaveBanner = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/banners/${editingItem.id}`, bannerForm);
        toast.success('Bannière mise à jour');
      } else {
        await api.post('/banners', bannerForm);
        toast.success('Bannière créée');
      }
      
      setBannerDialogOpen(false);
      setEditingItem(null);
      setBannerForm({ title: '', description: '', imageUrl: '', contactPhone: '', contactEmail: '', shopUrl: '', isActive: true });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Upload image pour bannière
  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10MB)');
      return;
    }
    
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Construire l'URL complète à partir de l'URL relative
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const imageUrl = response.data.url.startsWith('/') 
        ? `${baseUrl}${response.data.url}` 
        : response.data.url;
      
      setBannerForm(prev => ({ ...prev, imageUrl }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingBanner(false);
      if (bannerFileInputRef.current) bannerFileInputRef.current.value = '';
    }
  };

  const handleDeleteBanner = async (id) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteBannerDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/banners/${id}`);
      toast.success('Bannière supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Media CRUD (YouTube links only - photos via upload)
  const handleSaveMedia = async (e) => {
    e.preventDefault();
    try {
      const mediaToSave = {
        ...mediaForm,
        type: 'YOUTUBE_LINK' // Force YouTube link type for this form
      };
      await api.post('/media', mediaToSave);
      toast.success('Vidéo YouTube ajoutée');
      setMediaDialogOpen(false);
      setMediaForm({ type: 'YOUTUBE_LINK', title: '', url: '' });
      fetchAllData();
    } catch (error) {
      console.error('Media save error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  // File Upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux (max 50MB)');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Add media to database
      await api.post('/media', {
        type: response.data.type,
        title: file.name,
        url: response.data.url
      });
      
      toast.success('Fichier uploadé avec succès!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteMedia = async (id) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteMediaDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success('Média supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Message actions
  const handleMarkRead = async (id) => {
    try {
      await api.put(`/messages/${id}/read`);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, isRead: true } : m));
    } catch (error) {
      console.error('Error marking read:', error);
    }
  };

  // Manager CRUD (uniquement pour les artistes)
  const handleSaveManager = async (e) => {
    e.preventDefault();
    if (user?.role !== 'ARTISTE') {
      toast.error('Seul l\'artiste peut créer des managers');
      return;
    }
    
    try {
      const res = await api.post(`/artists/tenant/${user.tenantId}/managers`, managerForm);
      toast.success(`Manager créé ! Mot de passe par défaut: ${res.data.manager.defaultPassword}`);
      setManagerDialogOpen(false);
      setManagerForm({ email: '', firstName: '', lastName: '', phone: '' });
      fetchManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteManager = async (managerId) => {
    const confirmed = await confirm({
      title: t('deleteConfirmTitle'),
      message: t('deleteManagerDesc'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/artists/tenant/${user.tenantId}/managers/${managerId}`);
      toast.success('Manager supprimé');
      fetchManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Scanner CRUD (pour artistes et managers)
  const handleSaveScanner = async (e) => {
    e.preventDefault();
    if (!['ARTISTE', 'MANAGER'].includes(user?.role)) {
      toast.error('Seuls les artistes et managers peuvent créer des scanneurs');
      return;
    }
    
    try {
      const res = await api.post(`/artists/tenant/${user.tenantId}/scanners`, scannerForm);
      toast.success(`Scanneur créé ! Mot de passe par défaut: ${res.data.scanner.defaultPassword}`);
      setScannerDialogOpen(false);
      setScannerForm({ email: '', firstName: '', lastName: '', phone: '' });
      fetchScanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteScanner = async (scannerId) => {
    const confirmed = await confirm({
      title: 'Supprimer le scanneur',
      message: 'Êtes-vous sûr de vouloir supprimer ce compte scanneur ?',
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel')
    });
    if (!confirmed) return;
    try {
      await api.delete(`/artists/tenant/${user.tenantId}/scanners/${scannerId}`);
      toast.success('Scanneur supprimé');
      fetchScanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatPrice = (price, currency = 'XOF') => {
    const currencyCode = currency === 'FCFA' ? 'XOF' : currency;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode, minimumFractionDigits: 0 }).format(price);
  };
  const formatDate = (date) => format(new Date(date), 'dd MMM yyyy HH:mm', { locale: fr });

  // Vérifier si l'artiste est premium (depuis le tenant)
  const isPremium = tenant?.artist?.isPremium === true;
  
  const menuItems = [
    { id: 'dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard' },
    { id: 'events', icon: <CalendarIcon className="w-5 h-5" />, label: 'Événements' },
    { id: 'tickets', icon: <Ticket className="w-5 h-5" />, label: 'Billets' },
    { id: 'scanner', icon: <QrCode className="w-5 h-5" />, label: 'Scanner' },
    { id: 'posts', icon: <MessageSquare className="w-5 h-5" />, label: 'Actualités' },
    { id: 'media', icon: <Image className="w-5 h-5" />, label: 'Médias' },
    { id: 'messages', icon: <Mail className="w-5 h-5" />, label: 'Messages' },
    // Menu Publicités visible uniquement pour les artistes premium
    ...(isPremium ? [{ id: 'banners', icon: <Megaphone className="w-5 h-5" />, label: 'Publicités' }] : []),
    // Gestion des scanneurs - visible pour artistes ET managers
    ...(['ARTISTE', 'MANAGER'].includes(user?.role) ? [{ id: 'scanners', icon: <Users className="w-5 h-5" />, label: 'Scanneurs' }] : []),
    ...(user?.role === 'ARTISTE' ? [{ id: 'managers', icon: <Users className="w-5 h-5" />, label: 'Managers' }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen bg-card border-r border-border transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'}`}>
        <div className="h-full flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <SenArtisteLogo className="w-6 h-6" />
              {sidebarOpen && <span className="font-syne font-bold">Management</span>}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto min-h-0">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  // Fermer le menu mobile après sélection
                  if (window.innerWidth < 1024) setSidebarOpen(false);
                  if (item.id === 'scanner') navigate(`/${artistSlug}/scanner`);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${activeTab === item.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                data-testid={`menu-${item.id}`}
              >
                {item.icon}
                {sidebarOpen && <span>{item.label}</span>}
              </button>
            ))}
          </nav>
          
          {/* Section fixée en bas - toujours visible */}
          <div className="p-4 border-t border-border space-y-2 flex-shrink-0 bg-card">
            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => navigate(`/${artistSlug}`)}>
              <Eye className="w-5 h-5" />
              {sidebarOpen && 'Voir la fanpage'}
            </Button>
            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => navigate(`/${artistSlug}/account`)}>
              <Settings className="w-5 h-5" />
              {sidebarOpen && 'Paramètres'}
            </Button>
            <Button variant="ghost" className="w-full justify-start gap-3 text-destructive" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
              {sidebarOpen && 'Déconnexion'}
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
              <Menu className="w-5 h-5" />
            </Button>
            <h1 className="font-syne font-bold text-xl">{tenant?.artist?.nomDeScene || 'Management'}</h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <div className="flex items-center gap-2 ml-4">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-medium">{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
              </div>
              <span className="hidden sm:block text-sm">{user?.firstName}</span>
            </div>
          </div>
        </header>
        
        <div className="p-4 md:p-6">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6" data-testid="dashboard-content">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Fans</p>
                        <p className="text-3xl font-bold">{stats.totalFans || 0}</p>
                      </div>
                      <Users className="w-10 h-10 text-primary opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Événements</p>
                        <p className="text-3xl font-bold">{stats.totalEvents || 0}</p>
                      </div>
                      <CalendarIcon className="w-10 h-10 text-primary opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Billets vendus</p>
                        <p className="text-3xl font-bold">{stats.totalTickets || 0}</p>
                      </div>
                      <Ticket className="w-10 h-10 text-primary opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Messages</p>
                        <p className="text-3xl font-bold">{stats.unreadMessages || 0}</p>
                      </div>
                      <Mail className="w-10 h-10 text-primary opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Revenus par devise */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="glass-card border-l-4 border-l-green-500">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Revenus FCFA</p>
                        <p className="text-2xl font-bold text-green-500">{formatPrice(stats.revenueFCFA || 0, 'FCFA')}</p>
                        <p className="text-xs text-muted-foreground mt-1">Orange Money / Wave</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-green-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="glass-card border-l-4 border-l-blue-500">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Revenus EUR</p>
                        <p className="text-2xl font-bold text-blue-500">{formatPrice(stats.revenueEUR || 0, 'EUR')}</p>
                        <p className="text-xs text-muted-foreground mt-1">Carte Bancaire (SEPA)</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-blue-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Explication de la répartition des gains EUR */}
              <Card className="glass-card bg-gradient-to-r from-blue-500/5 to-transparent" data-testid="earnings-breakdown">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Info className="w-5 h-5 text-blue-500" />
                    Comment sont calculés vos gains EUR ?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                      <span className="text-muted-foreground">Votre part sur chaque billet</span>
                      <span className="font-bold text-blue-500">96% du prix affiché</span>
                    </div>
                    <div className="text-xs text-muted-foreground px-1">
                      <p className="mb-2"><strong>Exemple :</strong> Pour un billet vendu à 25€</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-blue-500/10 rounded">
                          <p className="font-medium">Vous recevez</p>
                          <p className="text-blue-500 font-bold">24,00€</p>
                        </div>
                        <div className="p-2 bg-muted/50 rounded">
                          <p className="font-medium">Commission plateforme</p>
                          <p className="text-muted-foreground">1,00€ (4%)</p>
                        </div>
                      </div>
                      <p className="mt-2 text-xs">
                        💡 Les frais de service (0,50€/billet) sont payés par l'acheteur en plus du prix du billet et ne réduisent pas vos gains.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Statut Stripe Connect */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Paiements Stripe
                  </CardTitle>
                  <CardDescription>
                    Configurez Stripe pour recevoir des paiements par carte bancaire pour vos événements en EUR
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {stripeStatus ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {stripeStatus.stripe_account_enabled ? (
                          <>
                            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-green-500" />
                            </div>
                            <div>
                              <p className="font-medium text-green-500">Compte Stripe actif</p>
                              <p className="text-sm text-muted-foreground">Vous pouvez recevoir des paiements EUR</p>
                            </div>
                          </>
                        ) : stripeStatus.stripe_account_id ? (
                          <>
                            <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-yellow-500" />
                            </div>
                            <div>
                              <p className="font-medium text-yellow-500">Configuration incomplète</p>
                              <p className="text-sm text-muted-foreground">Veuillez compléter votre inscription Stripe</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                              <CreditCard className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">Non configuré</p>
                              <p className="text-sm text-muted-foreground">Configurez Stripe pour les paiements EUR</p>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {!stripeStatus.stripe_account_enabled && (
                        <Button onClick={handleStripeOnboarding} className="btn-primary">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {stripeStatus.stripe_account_id ? 'Compléter' : 'Configurer Stripe'}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Chargement...</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Paiements FCFA (Wave / Orange Money) */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-600 text-xs font-bold">F</span>
                    Paiements FCFA (Wave / Orange Money)
                  </CardTitle>
                  <CardDescription>
                    {user?.role === 'ARTISTE' ? 'Configurez vos numéros pour recevoir les paiements en FCFA' : 'État de la configuration des paiements FCFA'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Toggle activation - seulement pour l'artiste */}
                  {user?.role === 'ARTISTE' && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        {tenant?.artist?.fcfa_payments_enabled !== false ? (
                          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-500" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                            <X className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className={`font-medium ${tenant?.artist?.fcfa_payments_enabled !== false ? 'text-green-500' : ''}`}>
                            {tenant?.artist?.fcfa_payments_enabled !== false ? 'Paiements FCFA activés' : 'Paiements FCFA désactivés'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {tenant?.artist?.fcfa_payments_enabled !== false ? 'Vos fans peuvent payer en FCFA' : 'Activez pour accepter les paiements FCFA'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={tenant?.artist?.fcfa_payments_enabled !== false}
                        onCheckedChange={async (enabled) => {
                          try {
                            await api.put(`/artists/tenant/${user.tenantId}`, { fcfa_payments_enabled: enabled });
                            toast.success(enabled ? 'Paiements FCFA activés' : 'Paiements FCFA désactivés');
                            fetchTenant(artistSlug);
                          } catch (error) {
                            toast.error('Erreur lors de la mise à jour');
                          }
                        }}
                        data-testid="fcfa-payments-toggle"
                      />
                    </div>
                  )}
                  
                  {/* Pastilles Orange Money et Wave */}
                  {tenant?.artist?.fcfa_payments_enabled !== false && (
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Orange Money */}
                      <div className={`p-3 rounded-lg ${tenant?.artist?.numeroOrangeMoney ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-muted/30 border border-muted'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className={`text-sm font-medium ${tenant?.artist?.numeroOrangeMoney ? 'text-orange-600' : 'text-muted-foreground'}`}>
                            Orange Money
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${tenant?.artist?.numeroOrangeMoney ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-500'}`}>
                            {tenant?.artist?.numeroOrangeMoney ? 'Configuré' : 'Non configuré'}
                          </span>
                        </div>
                        {tenant?.artist?.numeroOrangeMoney ? (
                          <p className="text-lg font-bold">{tenant?.artist?.numeroOrangeMoney}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Aucun numéro</p>
                        )}
                        {/* Champ de saisie seulement pour l'artiste */}
                        {user?.role === 'ARTISTE' && (
                          <div className="mt-2">
                            <Input
                              placeholder="+221 77 xxx xx xx"
                              defaultValue={tenant?.artist?.numeroOrangeMoney || ''}
                              onBlur={async (e) => {
                                if (e.target.value !== (tenant?.artist?.numeroOrangeMoney || '')) {
                                  try {
                                    await api.put(`/artists/tenant/${user.tenantId}`, { numeroOrangeMoney: e.target.value });
                                    toast.success('Numéro Orange Money mis à jour');
                                    fetchTenant(artistSlug);
                                  } catch (error) {
                                    toast.error('Erreur lors de la mise à jour');
                                  }
                                }
                              }}
                              className="text-sm"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Wave */}
                      <div className={`p-3 rounded-lg ${tenant?.artist?.numeroWave ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-muted/30 border border-muted'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className={`text-sm font-medium ${tenant?.artist?.numeroWave ? 'text-blue-600' : 'text-muted-foreground'}`}>
                            Wave
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${tenant?.artist?.numeroWave ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-500'}`}>
                            {tenant?.artist?.numeroWave ? 'Configuré' : 'Non configuré'}
                          </span>
                        </div>
                        {tenant?.artist?.numeroWave ? (
                          <p className="text-lg font-bold">{tenant?.artist?.numeroWave}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">Aucun numéro</p>
                        )}
                        {/* Champ de saisie seulement pour l'artiste */}
                        {user?.role === 'ARTISTE' && (
                          <div className="mt-2">
                            <Input
                              placeholder="+221 76 xxx xx xx"
                              defaultValue={tenant?.artist?.numeroWave || ''}
                              onBlur={async (e) => {
                                if (e.target.value !== (tenant?.artist?.numeroWave || '')) {
                                  try {
                                    await api.put(`/artists/tenant/${user.tenantId}`, { numeroWave: e.target.value });
                                    toast.success('Numéro Wave mis à jour');
                                    fetchTenant(artistSlug);
                                  } catch (error) {
                                    toast.error('Erreur lors de la mise à jour');
                                  }
                                }
                              }}
                              className="text-sm"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* QR Code de la page artiste */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-primary" />
                    Mon QR Code
                  </CardTitle>
                  <CardDescription>
                    Partagez votre page artiste avec un simple scan
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-4">
                  {tenant?.artist?.qrCodeUrl ? (
                    <>
                      <div className="p-4 bg-white rounded-lg">
                        <img 
                          src={tenant.artist.qrCodeUrl.startsWith('http') ? tenant.artist.qrCodeUrl : `${process.env.REACT_APP_BACKEND_URL}${tenant.artist.qrCodeUrl}`}
                          alt="QR Code"
                          className="w-32 h-32"
                          onError={(e) => {
                            // Si l'image ne charge pas, recharger les données pour regénérer
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground text-center">
                        Scannez pour accéder à votre fanpage
                      </p>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => setQrCodeDialogOpen(true)}
                        data-testid="show-qr-details-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Afficher les détails
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <QrCode className="w-16 h-16 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">QR Code en cours de génération...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Dialog QR Code Flyer */}
              <Dialog open={qrCodeDialogOpen} onOpenChange={setQrCodeDialogOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-center">Ma Carte Artiste</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg">
                    {/* Flyer style */}
                    <div className="bg-card border border-border rounded-xl p-6 shadow-lg w-full max-w-xs text-center" id="artist-flyer">
                      <h2 className="font-syne text-2xl font-bold mb-2" style={{ color: tenant?.artist?.primaryColor || '#D4AF37' }}>
                        {tenant?.artist?.nomDeScene || tenant?.artist?.name || 'Artiste'}
                      </h2>
                      
                      <div className="my-4 p-3 bg-white rounded-lg inline-block">
                        {tenant?.artist?.qrCodeUrl && (
                          <img 
                            src={tenant.artist.qrCodeUrl.startsWith('http') ? tenant.artist.qrCodeUrl : `${process.env.REACT_APP_BACKEND_URL}${tenant.artist.qrCodeUrl}`}
                            alt="QR Code"
                            className="w-40 h-40"
                          />
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-3">Scannez pour visiter ma page</p>
                      
                      <div className="p-2 bg-muted/50 rounded-lg">
                        <p className="text-sm font-mono break-all">
                          https://sen-artiste.com/{artistSlug}
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                      💡 Faites une capture d'écran pour partager ou imprimer
                    </p>
                  </div>
                  <DialogFooter className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setQrCodeDialogOpen(false)}>
                      Fermer
                    </Button>
                    <Button 
                      className="flex-1 btn-primary"
                      onClick={() => {
                        const url = `https://sen-artiste.com/${artistSlug}`;
                        navigator.clipboard.writeText(url);
                        toast.success('URL copiée !');
                      }}
                    >
                      Copier l'URL
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Messages non lus</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {messages.filter(m => !m.isRead).length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Aucun message non lu</p>
                    ) : (
                      <div className="space-y-3">
                        {messages.filter(m => !m.isRead).slice(0, 5).map(msg => (
                          <div key={msg.id} className="p-3 bg-muted/50 rounded-lg">
                            <p className="font-medium">{msg.subject}</p>
                            <p className="text-sm text-muted-foreground">{msg.senderName || msg.senderEmail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Prochains événements</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {events.filter(e => new Date(e.dateTime) > new Date()).length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Aucun événement à venir</p>
                    ) : (
                      <div className="space-y-3">
                        {events.filter(e => new Date(e.dateTime) > new Date()).slice(0, 5).map(evt => (
                          <div key={evt.id} className="p-3 bg-muted/50 rounded-lg flex justify-between items-center">
                            <div>
                              <p className="font-medium">{evt.title}</p>
                              <p className="text-sm text-muted-foreground">{formatDate(evt.dateTime)}</p>
                            </div>
                            <span className="text-sm font-medium text-primary">{evt.ticketsSold || 0} vendus</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-6" data-testid="events-content">
              <div className="flex justify-between items-center">
                <h2 className="font-syne text-2xl font-bold">Événements</h2>
                <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={() => { setEditingItem(null); resetEventForm(); }} data-testid="add-event-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingItem ? 'Modifier' : 'Nouvel'} événement</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveEvent} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Titre</Label>
                        <Input value={eventForm.title} onChange={(e) => setEventForm(p => ({ ...p, title: e.target.value }))} required data-testid="event-title" />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea value={eventForm.description} onChange={(e) => setEventForm(p => ({ ...p, description: e.target.value }))} rows={3} data-testid="event-description" />
                        <AIWriter
                          api={api}
                          field="event_description"
                          value={eventForm.description}
                          onApply={(text) => setEventForm(p => ({ ...p, description: text }))}
                          context={{
                            nomDeScene: tenant?.artist?.nomDeScene,
                            genreMusical: tenant?.artist?.genreMusical,
                            title: eventForm.title,
                            date: eventForm.dateTime ? format(eventForm.dateTime, 'dd/MM/yyyy HH:mm') : '',
                            location: eventForm.location,
                            currency: eventForm.currency,
                            ticketTypes: (eventForm.ticketTypes || []).filter(tt => tt.name && tt.price).map(tt => `${tt.name} : ${tt.price} ${eventForm.currency}`),
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date et heure</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start">
                                <CalendarIcon className="w-4 h-4 mr-2" />
                                {format(eventForm.dateTime, 'dd/MM/yyyy HH:mm')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={eventForm.dateTime}
                                onSelect={(date) => {
                                  if (date) {
                                    // Conserver l'heure existante lors du changement de date
                                    const newDate = new Date(date);
                                    newDate.setHours(eventForm.dateTime.getHours());
                                    newDate.setMinutes(eventForm.dateTime.getMinutes());
                                    setEventForm(p => ({ ...p, dateTime: newDate }));
                                  }
                                }}
                              />
                              {/* Sélecteur d'heure */}
                              <div className="border-t p-3">
                                <Label className="text-xs text-muted-foreground mb-2 block">Heure de l'événement</Label>
                                <div className="flex gap-2 items-center">
                                  <select 
                                    className="flex h-9 w-20 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                    value={eventForm.dateTime.getHours()}
                                    onChange={(e) => {
                                      const newDate = new Date(eventForm.dateTime);
                                      newDate.setHours(parseInt(e.target.value));
                                      setEventForm(p => ({ ...p, dateTime: newDate }));
                                    }}
                                  >
                                    {Array.from({length: 24}, (_, i) => (
                                      <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                                    ))}
                                  </select>
                                  <span className="text-lg font-bold">:</span>
                                  <select 
                                    className="flex h-9 w-20 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                    value={eventForm.dateTime.getMinutes()}
                                    onChange={(e) => {
                                      const newDate = new Date(eventForm.dateTime);
                                      newDate.setMinutes(parseInt(e.target.value));
                                      setEventForm(p => ({ ...p, dateTime: newDate }));
                                    }}
                                  >
                                    {[0, 15, 30, 45].map(m => (
                                      <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>Devise du billet *</Label>
                          <Select 
                            value={eventForm.currency} 
                            onValueChange={(v) => setEventForm(p => ({ ...p, currency: v }))}
                          >
                            <SelectTrigger data-testid="event-currency">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FCFA">FCFA (Orange Money / Wave)</SelectItem>
                              <SelectItem value="EUR">EUR (Carte Bancaire)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Types de billets multiples */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-semibold">Types de billets</Label>
                          {eventForm.ticketTypes.length < 5 && (
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm" 
                              onClick={addTicketType}
                              data-testid="add-ticket-type-btn"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Ajouter un type
                            </Button>
                          )}
                        </div>
                        
                        {eventForm.ticketTypes.map((ticketType, index) => (
                          <div key={index} className="p-4 border rounded-lg space-y-3 bg-muted/30">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">Type {index + 1}</span>
                              {eventForm.ticketTypes.length > 1 && (
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => removeTicketType(index)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Nom *</Label>
                                <Input 
                                  value={ticketType.name} 
                                  onChange={(e) => updateTicketType(index, 'name', e.target.value)}
                                  placeholder="Ex: VIP, Standard..."
                                  data-testid={`ticket-type-name-${index}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Prix ({eventForm.currency}) *</Label>
                                <Input 
                                  type="number" 
                                  value={ticketType.price} 
                                  onChange={(e) => updateTicketType(index, 'price', e.target.value)}
                                  placeholder="0"
                                  data-testid={`ticket-type-price-${index}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Quantité</Label>
                                <Input 
                                  type="number" 
                                  value={ticketType.quantity} 
                                  onChange={(e) => updateTicketType(index, 'quantity', e.target.value)}
                                  placeholder="Illimité"
                                  data-testid={`ticket-type-quantity-${index}`}
                                />
                              </div>
                            </div>
                            {ticketType.price && (
                              <p className="text-xs text-green-600 dark:text-green-400">
                                💰 Vous recevrez <strong>{calculateNetAmount(ticketType.price, eventForm.commissionPaidBy).toLocaleString()} {eventForm.currency}</strong> par billet vendu
                              </p>
                            )}
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground">
                          Ajoutez jusqu'à 5 types de billets différents (Standard, VIP, Carré Or, etc.)
                        </p>
                      </div>
                      
                      {/* Choix de qui paie la commission */}
                      <div className="p-4 border rounded-lg space-y-3 bg-amber-500/10 border-amber-500/30">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Qui paie la commission de 5% ?
                        </Label>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                            <input 
                              type="radio" 
                              name="commissionPaidBy" 
                              value="artist"
                              checked={eventForm.commissionPaidBy === 'artist'}
                              onChange={(e) => setEventForm(p => ({ ...p, commissionPaidBy: e.target.value }))}
                              className="mt-1"
                              data-testid="commission-paid-by-artist"
                            />
                            <div>
                              <span className="font-medium">Moi (l'artiste)</span>
                              <p className="text-xs text-muted-foreground">
                                Le fan paie le prix affiché. Vous recevez 95% du prix.
                              </p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                            <input 
                              type="radio" 
                              name="commissionPaidBy" 
                              value="fan"
                              checked={eventForm.commissionPaidBy === 'fan'}
                              onChange={(e) => setEventForm(p => ({ ...p, commissionPaidBy: e.target.value }))}
                              className="mt-1"
                              data-testid="commission-paid-by-fan"
                            />
                            <div>
                              <span className="font-medium">Le fan (acheteur)</span>
                              <p className="text-xs text-muted-foreground">
                                Le fan paie le prix + 5% de frais. Vous recevez 100% du prix affiché.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Lieu</Label>
                        <Input value={eventForm.location} onChange={(e) => setEventForm(p => ({ ...p, location: e.target.value }))} data-testid="event-location" />
                      </div>
                      <div className="space-y-2">
                        <Label>Nombre de places max (optionnel)</Label>
                        <Input 
                          type="number" 
                          min="0"
                          placeholder="Illimité si vide"
                          value={eventForm.maxCapacity} 
                          onChange={(e) => setEventForm(p => ({ ...p, maxCapacity: e.target.value }))} 
                          data-testid="event-max-capacity" 
                        />
                        <p className="text-xs text-muted-foreground">Laissez vide pour un nombre illimité de billets</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={eventForm.isPublished} onCheckedChange={(v) => setEventForm(p => ({ ...p, isPublished: v }))} />
                        <Label>Publier immédiatement</Label>
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="btn-primary" data-testid="event-submit">Enregistrer</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="grid gap-4">
                {events.map(event => (
                  <Card key={event.id} className="glass-card" data-testid={`event-item-${event.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold">{event.title}</h3>
                            {event.isPublished ? (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600">Publié</span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-600">Brouillon</span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${event.currency === 'EUR' ? 'bg-blue-500/20 text-blue-600' : 'bg-green-500/20 text-green-600'}`}>
                              {event.currency || 'FCFA'}
                            </span>
                            {event.maxCapacity && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${event.ticketsSold >= event.maxCapacity ? 'bg-red-500/20 text-red-600' : 'bg-purple-500/20 text-purple-600'}`}>
                                {event.ticketsSold || 0}/{event.maxCapacity} places
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{formatDate(event.dateTime)} • {event.location}</p>
                          <p className="text-sm font-medium text-primary mt-1">
                            {formatPrice(event.ticketPrice, event.currency || 'FCFA')}
                            {!event.maxCapacity && <span className="text-xs text-muted-foreground ml-2">• {event.ticketsSold || 0} vendu(s)</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditingItem(event); setEventForm({ ...event, dateTime: new Date(event.dateTime), currency: event.currency || 'FCFA', maxCapacity: event.maxCapacity || '' }); setEventDialogOpen(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setActiveTab('tickets'); fetchEventTickets(event.id); }}>
                            <Ticket className="w-4 h-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteEvent(event.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Stats de gains */}
                      {event.earnings && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          {/* Stats générales */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div className="p-2 bg-muted/30 rounded-lg text-center">
                              <p className="text-xs text-muted-foreground">Billets vendus</p>
                              <p className="font-bold text-lg">{event.earnings.totalTicketsSold}</p>
                            </div>
                            <div className="p-2 bg-blue-500/10 rounded-lg text-center">
                              <p className="text-xs text-muted-foreground">Total généré</p>
                              <p className="font-bold text-lg text-blue-600">
                                {event.currency === 'EUR' 
                                  ? `${event.earnings.totalEarned.toFixed(2)}€`
                                  : `${event.earnings.totalEarned.toLocaleString()} FCFA`
                                }
                              </p>
                            </div>
                            <div className="p-2 bg-green-500/10 rounded-lg text-center">
                              <p className="text-xs text-muted-foreground">Disponible</p>
                              <p className="font-bold text-lg text-green-600">
                                {event.currency === 'EUR' 
                                  ? `${event.earnings.availableToWithdraw.toFixed(2)}€`
                                  : `${event.earnings.availableToWithdraw.toLocaleString()} FCFA`
                                }
                              </p>
                            </div>
                            <div className="p-2 bg-purple-500/10 rounded-lg text-center">
                              <p className="text-xs text-muted-foreground">Déjà reversé</p>
                              <p className="font-bold text-lg text-purple-600">
                                {event.currency === 'EUR' 
                                  ? `${event.earnings.totalWithdrawn.toFixed(2)}€`
                                  : `${event.earnings.totalWithdrawn.toLocaleString()} FCFA`
                                }
                              </p>
                            </div>
                          </div>
                          
                          {/* Détails Wave / Orange pour événements FCFA */}
                          {event.currency !== 'EUR' && event.earnings.wave && event.earnings.orange && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                              {/* Wave */}
                              <div className={`p-3 rounded-lg border ${event.earnings.wave.available > 0 ? 'bg-blue-500/5 border-blue-500/30' : 'bg-muted/20 border-border/50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-bold">W</span>
                                    Wave
                                  </span>
                                  <span className="text-xs text-muted-foreground">{event.earnings.wave.ticketsSold} billet(s)</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div>
                                    <p className="text-muted-foreground">Généré</p>
                                    <p className="font-medium">{event.earnings.wave.earned.toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Disponible</p>
                                    <p className="font-medium text-green-600">{event.earnings.wave.available.toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Reversé</p>
                                    <p className="font-medium text-purple-600">{event.earnings.wave.withdrawn.toLocaleString()}</p>
                                  </div>
                                </div>
                                {event.earnings.wave.pendingManualPayout ? (
                                  <div className="w-full mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-center">
                                    <span className="text-yellow-600 dark:text-yellow-400 text-xs font-medium">
                                      ⏳ Reversement en cours de traitement
                                    </span>
                                  </div>
                                ) : event.earnings.wave.available > 0 && artistWithdrawalInfo?.waveNumber && (
                                  <Button
                                    size="sm"
                                    className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                                    onClick={() => {
                                      setReversementEventId(event.id);
                                      setReversementMethod('wave');
                                      setReversementDialogOpen(true);
                                    }}
                                  >
                                    Reverser {event.earnings.wave.available.toLocaleString()} FCFA sur Wave
                                  </Button>
                                )}
                              </div>
                              
                              {/* Orange Money */}
                              <div className={`p-3 rounded-lg border ${event.earnings.orange.available > 0 ? 'bg-orange-500/5 border-orange-500/30' : 'bg-muted/20 border-border/50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-orange-600 dark:text-orange-400 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">OM</span>
                                    Orange Money
                                  </span>
                                  <span className="text-xs text-muted-foreground">{event.earnings.orange.ticketsSold} billet(s)</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div>
                                    <p className="text-muted-foreground">Généré</p>
                                    <p className="font-medium">{event.earnings.orange.earned.toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Disponible</p>
                                    <p className="font-medium text-green-600">{event.earnings.orange.available.toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Reversé</p>
                                    <p className="font-medium text-purple-600">{event.earnings.orange.withdrawn.toLocaleString()}</p>
                                  </div>
                                </div>
                                {event.earnings.orange.pendingManualPayout ? (
                                  <div className="w-full mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-center">
                                    <span className="text-yellow-600 dark:text-yellow-400 text-xs font-medium">
                                      ⏳ Reversement en cours de traitement
                                    </span>
                                  </div>
                                ) : event.earnings.orange.available > 0 && artistWithdrawalInfo?.orangeMoneyNumber && (
                                  <Button
                                    size="sm"
                                    className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white"
                                    onClick={() => {
                                      setReversementEventId(event.id);
                                      setReversementMethod('orange-money');
                                      setReversementDialogOpen(true);
                                    }}
                                  >
                                    Reverser {event.earnings.orange.available.toLocaleString()} FCFA sur Orange Money
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Message si pas de numéro configuré */}
                          {event.currency !== 'EUR' && event.earnings.availableToWithdraw > 0 && 
                           !artistWithdrawalInfo?.waveNumber && !artistWithdrawalInfo?.orangeMoneyNumber && (
                            <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-yellow-600" />
                                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
                                  Configurez vos numéros Wave ou Orange Money pour recevoir vos reversements
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2"
                                onClick={() => setActiveTab('dashboard')}
                              >
                                Configurer mes numéros
                              </Button>
                            </div>
                          )}
                          
                          {/* EUR - reversement manuel */}
                          {event.currency === 'EUR' && (
                            <div className="flex flex-col gap-2 mt-2 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                              <div className="flex items-center gap-2">
                                <Info className="w-4 h-4 text-blue-500" />
                                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                  Reversement EUR sous 10 jours
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Le virement EUR sera effectué par l'équipe Sen Artiste sur votre compte bancaire configuré.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="space-y-6" data-testid="tickets-content">
              <div className="flex justify-between items-center">
                <h2 className="font-syne text-2xl font-bold">Billets</h2>
                <Select onValueChange={(eventId) => fetchEventTickets(eventId)}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Sélectionner un événement" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {tickets.length === 0 ? (
                <Card className="glass-card">
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">Sélectionnez un événement pour voir les billets</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {tickets.map(ticket => (
                    <Card key={ticket.id} className="glass-card" data-testid={`ticket-item-${ticket.id}`}>
                      <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <p className="font-mono text-lg font-bold">{ticket.ticketNumber}</p>
                          <p className="text-sm text-muted-foreground">{ticket.buyerName} • {ticket.buyerEmail}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(ticket.purchasedAt)}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            ticket.status === 'VALID' ? 'badge-valid' :
                            ticket.status === 'USED' ? 'badge-used' : 'badge-cancelled'
                          }`}>
                            {ticket.status === 'VALID' ? 'Valide' : ticket.status === 'USED' ? 'Utilisé' : 'Annulé'}
                          </span>
                          {ticket.qrCodeData && (
                            <img src={`data:image/png;base64,${ticket.qrCodeData}`} alt="QR" className="w-16 h-16" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Posts Tab */}
          {activeTab === 'posts' && (
            <div className="space-y-6" data-testid="posts-content">
              <div className="flex justify-between items-center">
                <h2 className="font-syne text-2xl font-bold">Actualités</h2>
                <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={() => { setEditingItem(null); setPostForm({ title: '', content: '', isPublished: false }); }} data-testid="add-post-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingItem ? 'Modifier' : 'Nouvelle'} actualité</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSavePost} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Titre</Label>
                        <Input value={postForm.title} onChange={(e) => setPostForm(p => ({ ...p, title: e.target.value }))} required data-testid="post-title" />
                      </div>
                      <div className="space-y-2">
                        <Label>Contenu</Label>
                        <Textarea value={postForm.content} onChange={(e) => setPostForm(p => ({ ...p, content: e.target.value }))} rows={5} required data-testid="post-content" />
                        <AIWriter
                          api={api}
                          field="post"
                          value={postForm.content}
                          onApply={(text) => setPostForm(p => ({ ...p, content: text }))}
                          context={{
                            nomDeScene: tenant?.artist?.nomDeScene,
                            genreMusical: tenant?.artist?.genreMusical,
                            postTitle: postForm.title,
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={postForm.isPublished} onCheckedChange={(v) => setPostForm(p => ({ ...p, isPublished: v }))} />
                        <Label>Publier immédiatement</Label>
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="btn-primary" data-testid="post-submit">Enregistrer</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="grid gap-4">
                {posts.map(post => (
                  <Card key={post.id} className="glass-card" data-testid={`post-item-${post.id}`}>
                    <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold">{post.title}</h3>
                          {post.isPublished ? (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600">Publié</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-600">Brouillon</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{post.content}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingItem(post); setPostForm(post); setPostDialogOpen(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDeletePost(post.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6" data-testid="media-content">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="font-syne text-2xl font-bold">Médias</h2>
                <div className="flex gap-2">
                  {/* Upload Button */}
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*,video/*"
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-file-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Upload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Uploader
                      </>
                    )}
                  </Button>
                  
                  <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="add-media-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Lien YouTube
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Ajouter un lien YouTube</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveMedia} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Titre</Label>
                          <Input value={mediaForm.title} onChange={(e) => setMediaForm(p => ({ ...p, title: e.target.value }))} data-testid="media-title" placeholder="Titre de la vidéo" />
                        </div>
                        <div className="space-y-2">
                          <Label>Lien YouTube</Label>
                          <Input 
                            value={mediaForm.url} 
                            onChange={(e) => setMediaForm(p => ({ ...p, url: e.target.value, type: 'YOUTUBE_LINK' }))} 
                            required 
                            placeholder="https://www.youtube.com/watch?v=..." 
                            data-testid="media-url" 
                          />
                          <p className="text-xs text-muted-foreground">
                            Formats acceptés: youtube.com/watch?v=..., youtu.be/..., youtube.com/embed/...
                          </p>
                        </div>
                        <DialogFooter>
                          <Button type="submit" className="btn-primary" data-testid="media-submit">Ajouter</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              
              {/* Upload zone info */}
              <Card className="glass-card border-dashed border-2 border-primary/30">
                <CardContent className="p-4 text-center">
                  <Upload className="w-8 h-8 mx-auto text-primary/50 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    <strong>Images:</strong> JPG, PNG, GIF, WEBP (via URL ou upload)<br/>
                    <strong>Vidéos:</strong> Uniquement via liens YouTube (pas d'upload vidéo)
                  </p>
                </CardContent>
              </Card>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {media.map(item => (
                  <Card key={item.id} className="glass-card overflow-hidden group" data-testid={`media-item-${item.id}`}>
                    <div className="aspect-square relative">
                      {item.type === 'PHOTO' ? (
                        <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <Video className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteMedia(item.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {item.title && (
                      <CardContent className="p-2">
                        <p className="text-sm truncate">{item.title}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="space-y-6" data-testid="messages-content">
              <h2 className="font-syne text-2xl font-bold">Messages ({messages.filter(m => !m.isRead).length} non lus)</h2>
              
              <div className="space-y-4">
                {messages.map(msg => (
                  <Card key={msg.id} className={`glass-card ${!msg.isRead ? 'border-l-4 border-l-primary' : ''}`} data-testid={`message-item-${msg.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold">{msg.subject}</h3>
                          <p className="text-sm text-muted-foreground">{msg.senderName || 'Anonyme'} • {msg.senderEmail}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{formatDate(msg.sentAt)}</span>
                          {!msg.isRead && (
                            <Button variant="ghost" size="sm" onClick={() => handleMarkRead(msg.id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-muted-foreground whitespace-pre-wrap">{msg.body}</p>
                      {msg.reply && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Votre réponse:</p>
                          <p className="text-sm">{msg.reply.body}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Banners Tab */}
          {activeTab === 'banners' && (
            <div className="space-y-6" data-testid="banners-content">
              <div className="flex justify-between items-center">
                <h2 className="font-syne text-2xl font-bold">Publicités</h2>
                <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={() => { setEditingItem(null); setBannerForm({ title: '', description: '', imageUrl: '', contactPhone: '', contactEmail: '', shopUrl: '', isActive: true }); }} data-testid="add-banner-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingItem ? 'Modifier' : 'Nouvelle'} bannière</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveBanner} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Titre *</Label>
                        <Input value={bannerForm.title} onChange={(e) => setBannerForm(p => ({ ...p, title: e.target.value }))} required data-testid="banner-title" />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea value={bannerForm.description} onChange={(e) => setBannerForm(p => ({ ...p, description: e.target.value }))} rows={2} data-testid="banner-description" />
                        <AIWriter
                          api={api}
                          field="banner"
                          value={bannerForm.description}
                          onApply={(text) => setBannerForm(p => ({ ...p, description: text }))}
                          context={{ nomDeScene: tenant?.artist?.nomDeScene, title: bannerForm.title, shopUrl: bannerForm.shopUrl }}
                        />
                      </div>
                      
                      {/* Upload d'image pour bannière */}
                      <div className="space-y-2">
                        <Label>Image de la bannière *</Label>
                        <input
                          type="file"
                          ref={bannerFileInputRef}
                          onChange={handleBannerImageUpload}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          className="hidden"
                        />
                        
                        {bannerForm.imageUrl ? (
                          <div className="relative">
                            <div className="w-full h-32 rounded-lg border border-border bg-muted/50 flex items-center justify-center overflow-hidden">
                              <img 
                                src={bannerForm.imageUrl} 
                                alt="Aperçu bannière" 
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="absolute bottom-2 right-2"
                              onClick={() => bannerFileInputRef.current?.click()}
                              disabled={uploadingBanner}
                            >
                              {uploadingBanner ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-1" />
                                  Changer
                                </>
                              )}
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                            onClick={() => bannerFileInputRef.current?.click()}
                          >
                            {uploadingBanner ? (
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                <p className="text-sm text-muted-foreground">Upload en cours...</p>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-2">
                                <ImageIcon className="w-8 h-8 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">
                                  Cliquez pour uploader une image
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  JPG, PNG, GIF, WEBP (max 10MB)
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="border-t border-border pt-4 mt-4">
                        <p className="text-sm font-medium mb-3 text-muted-foreground">Informations de contact (optionnel)</p>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label>Téléphone (avec indicatif)</Label>
                            <Input 
                              value={bannerForm.contactPhone} 
                              onChange={(e) => setBannerForm(p => ({ ...p, contactPhone: e.target.value }))} 
                              placeholder="+221 77 123 45 67" 
                              data-testid="banner-phone" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input 
                              type="email"
                              value={bannerForm.contactEmail} 
                              onChange={(e) => setBannerForm(p => ({ ...p, contactEmail: e.target.value }))} 
                              placeholder="contact@exemple.com" 
                              data-testid="banner-email" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>URL du site web</Label>
                            <Input 
                              value={bannerForm.shopUrl} 
                              onChange={(e) => setBannerForm(p => ({ ...p, shopUrl: e.target.value }))} 
                              placeholder="https://monsite.com" 
                              data-testid="banner-shopUrl" 
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch checked={bannerForm.isActive} onCheckedChange={(v) => setBannerForm(p => ({ ...p, isActive: v }))} />
                        <Label>Active</Label>
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="btn-primary" disabled={!bannerForm.imageUrl || uploadingBanner} data-testid="banner-submit">Enregistrer</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                {banners.map(banner => (
                  <Card key={banner.id} className="glass-card overflow-hidden" data-testid={`banner-item-${banner.id}`}>
                    {banner.imageUrl && (
                      <div className="h-32">
                        <img src={banner.imageUrl} alt={banner.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold">{banner.title}</h3>
                        {banner.isActive ? (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-500/20 text-gray-600">Inactive</span>
                        )}
                      </div>
                      {banner.description && <p className="text-sm text-muted-foreground mb-3">{banner.description}</p>}
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingItem(banner); setBannerForm(banner); setBannerDialogOpen(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteBanner(banner.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Managers Tab - Only for ARTISTE role */}
          {activeTab === 'managers' && user?.role === 'ARTISTE' && (
            <div className="space-y-6" data-testid="managers-content">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-syne text-2xl font-bold">Gestionnaires (Managers)</h2>
                  <p className="text-sm text-muted-foreground">
                    {stats.managersCount || 0} / {stats.maxManagers || 3} managers créés
                  </p>
                </div>
                <Dialog open={managerDialogOpen} onOpenChange={setManagerDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary" 
                      disabled={(stats.managersCount || 0) >= 3}
                      onClick={() => { setManagerForm({ email: '', firstName: '', lastName: '', phone: '' }); fetchManagers(); }} 
                      data-testid="add-manager-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un manager
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Créer un compte manager</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveManager} className="space-y-4">
                      <div className="p-4 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium mb-2">Mot de passe par défaut:</p>
                        <p className="text-muted-foreground">
                          Le manager recevra le mot de passe: <strong>{tenant?.artist?.prenom?.charAt(0).toUpperCase() + tenant?.artist?.prenom?.slice(1)}{new Date().getFullYear()}</strong>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Il devra le changer à la première connexion.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input 
                          type="email"
                          value={managerForm.email} 
                          onChange={(e) => setManagerForm(p => ({ ...p, email: e.target.value }))} 
                          required 
                          placeholder="manager@exemple.com"
                          data-testid="manager-email" 
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Prénom *</Label>
                          <Input 
                            value={managerForm.firstName} 
                            onChange={(e) => setManagerForm(p => ({ ...p, firstName: e.target.value }))} 
                            required 
                            data-testid="manager-firstName" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nom *</Label>
                          <Input 
                            value={managerForm.lastName} 
                            onChange={(e) => setManagerForm(p => ({ ...p, lastName: e.target.value }))} 
                            required 
                            data-testid="manager-lastName" 
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input 
                          value={managerForm.phone} 
                          onChange={(e) => setManagerForm(p => ({ ...p, phone: e.target.value }))} 
                          placeholder="+221 77 xxx xx xx"
                          data-testid="manager-phone" 
                        />
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="btn-primary" data-testid="manager-submit">
                          Créer le manager
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Info card */}
              <Card className="glass-card border-l-4 border-l-primary">
                <CardContent className="p-4">
                  <p className="text-sm">
                    <strong>Limite:</strong> Vous pouvez créer jusqu'à 3 comptes managers. 
                    Les managers ont les mêmes droits que vous (sauf la création de managers).
                  </p>
                </CardContent>
              </Card>
              
              {/* Managers list */}
              <div className="grid gap-4">
                {managers.length === 0 ? (
                  <Card className="glass-card">
                    <CardContent className="p-8 text-center">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Aucun manager créé</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Cliquez sur "Ajouter un manager" pour commencer.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  managers.map(manager => (
                    <Card key={manager.id} className="glass-card" data-testid={`manager-item-${manager.id}`}>
                      <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-lg font-medium">
                              {manager.firstName?.[0]}{manager.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-bold">{manager.firstName} {manager.lastName}</h3>
                            <p className="text-sm text-muted-foreground">{manager.email}</p>
                            {manager.phone && <p className="text-xs text-muted-foreground">{manager.phone}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {manager.mustChangePassword ? (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-600">
                                Doit changer son mdp
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600">
                                Actif
                              </span>
                            )}
                            {!manager.createdByArtist && (
                              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-600">
                                Système
                              </span>
                            )}
                          </div>
                          {manager.createdByArtist && (
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteManager(manager.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Scanners Tab - For ARTISTE and MANAGER roles */}
          {activeTab === 'scanners' && ['ARTISTE', 'MANAGER'].includes(user?.role) && (
            <div className="space-y-6" data-testid="scanners-content">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-syne text-2xl font-bold">Scanneurs</h2>
                  <p className="text-sm text-muted-foreground">
                    {scanners.length} / 3 scanneurs créés
                  </p>
                </div>
                <Dialog open={scannerDialogOpen} onOpenChange={setScannerDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary" 
                      disabled={scanners.length >= 3}
                      onClick={() => { setScannerForm({ email: '', firstName: '', lastName: '', phone: '' }); fetchScanners(); }} 
                      data-testid="add-scanner-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un scanneur
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Créer un compte scanneur</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveScanner} className="space-y-4">
                      <div className="p-4 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium mb-2">Rôle limité:</p>
                        <p className="text-muted-foreground">
                          Le scanneur pourra <strong>uniquement scanner les billets</strong> lors des événements. Aucun accès aux autres fonctionnalités.
                        </p>
                        <p className="text-muted-foreground mt-2">
                          Mot de passe: <strong>{tenant?.artist?.prenom?.charAt(0).toUpperCase() + tenant?.artist?.prenom?.slice(1)}{new Date().getFullYear()}</strong>
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input 
                          type="email"
                          value={scannerForm.email} 
                          onChange={(e) => setScannerForm(p => ({ ...p, email: e.target.value }))} 
                          required 
                          placeholder="scanneur@exemple.com"
                          data-testid="scanner-email" 
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Prénom *</Label>
                          <Input 
                            value={scannerForm.firstName} 
                            onChange={(e) => setScannerForm(p => ({ ...p, firstName: e.target.value }))} 
                            required 
                            data-testid="scanner-firstName" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nom *</Label>
                          <Input 
                            value={scannerForm.lastName} 
                            onChange={(e) => setScannerForm(p => ({ ...p, lastName: e.target.value }))} 
                            required 
                            data-testid="scanner-lastName" 
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input 
                          value={scannerForm.phone} 
                          onChange={(e) => setScannerForm(p => ({ ...p, phone: e.target.value }))} 
                          placeholder="+221 77 xxx xx xx"
                          data-testid="scanner-phone" 
                        />
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="btn-primary" data-testid="scanner-submit">
                          Créer le scanneur
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Info card */}
              <Card className="glass-card border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-sm">
                    <strong>Scanneurs:</strong> Comptes limités au scan de billets uniquement. 
                    Idéal pour le personnel à l'entrée de vos événements. Maximum 3 comptes.
                  </p>
                </CardContent>
              </Card>
              
              {/* Scanners list */}
              <div className="grid gap-4">
                {scanners.length === 0 ? (
                  <Card className="glass-card">
                    <CardContent className="p-8 text-center">
                      <QrCode className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Aucun scanneur créé</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Créez des comptes scanneurs pour votre équipe à l'entrée.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  scanners.map(scanner => (
                    <Card key={scanner.id} className="glass-card" data-testid={`scanner-item-${scanner.id}`}>
                      <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <QrCode className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-bold">{scanner.firstName} {scanner.lastName}</h3>
                            <p className="text-sm text-muted-foreground">{scanner.email}</p>
                            {scanner.phone && <p className="text-xs text-muted-foreground">{scanner.phone}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {scanner.mustChangePassword ? (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-600">
                                Doit changer son mdp
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600">
                                Actif
                              </span>
                            )}
                          </div>
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteScanner(scanner.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Dialog Reversement DKR */}
      <Dialog open={reversementDialogOpen} onOpenChange={setReversementDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demander mon reversement FCFA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(() => {
              const event = events.find(e => e.id === reversementEventId);
              const methodData = reversementMethod === 'wave' 
                ? event?.earnings?.wave 
                : event?.earnings?.orange;
              return (
                <>
                  {event && methodData && methodData.available > 0 && (
                    <div className={`p-4 rounded-lg border ${
                      reversementMethod === 'wave' 
                        ? 'bg-blue-500/10 border-blue-500/30' 
                        : 'bg-orange-500/10 border-orange-500/30'
                    }`}>
                      <p className="text-lg font-bold text-center">
                        {methodData.available.toLocaleString()} FCFA
                      </p>
                      <p className="text-xs text-center text-muted-foreground mt-1">
                        à recevoir sur {reversementMethod === 'wave' ? 'Wave' : 'Orange Money'}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Méthode de reversement</Label>
                    <Select value={reversementMethod} onValueChange={setReversementMethod}>
                      <SelectTrigger data-testid="reversement-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {artistWithdrawalInfo?.waveNumber && event?.earnings?.wave?.available > 0 && (
                          <SelectItem value="wave">
                            Wave — {artistWithdrawalInfo.waveNumber} ({event.earnings.wave.available.toLocaleString()} FCFA)
                          </SelectItem>
                        )}
                        {artistWithdrawalInfo?.orangeMoneyNumber && event?.earnings?.orange?.available > 0 && (
                          <SelectItem value="orange-money">
                            Orange Money — {artistWithdrawalInfo.orangeMoneyNumber} ({event.earnings.orange.available.toLocaleString()} FCFA)
                          </SelectItem>
                        )}
                        {!artistWithdrawalInfo?.waveNumber && !artistWithdrawalInfo?.orangeMoneyNumber && (
                          <SelectItem value="none" disabled>Aucun numéro configuré</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              );
            })()}
            {!artistWithdrawalInfo?.waveNumber && !artistWithdrawalInfo?.orangeMoneyNumber && (
              <p className="text-sm text-destructive">
                Aucun numéro Wave ou Orange Money trouvé. Veuillez les ajouter dans les Paramètres.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReversementDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              className={`text-white ${
                reversementMethod === 'wave' 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
              onClick={handleRequestReversement}
              disabled={reversementLoading || (!artistWithdrawalInfo?.waveNumber && !artistWithdrawalInfo?.orangeMoneyNumber)}
              data-testid="reversement-confirm-btn"
            >
              {reversementLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {reversementLoading ? 'Envoi en cours...' : 'Confirmer le reversement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Management;
